import React from "react";
import { LocaSmartLayout } from "../../components/locasmart-layout";
import { ExternalLink, BookOpen, PlayCircle, FileText, MessageCircle, Headphones } from "lucide-react";
import logoIA from "../../assets/logo-ia.png";

function AjudaView() {
    const trainingUrl = "https://treinamento.locasmartpro.com.br";

    const abrirAssistente = () => {
        window.dispatchEvent(new Event('open-help-bot'));
    };

    return (
        <LocaSmartLayout>
            <style>
                {`
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fade-in-up {
                        animation: fadeInUp 0.6s ease-out forwards;
                    }
                    .ajuda-delay-1 { animation-delay: 0.1s; opacity: 0; }
                    .ajuda-delay-2 { animation-delay: 0.25s; opacity: 0; }
                    .ajuda-delay-3 { animation-delay: 0.4s; opacity: 0; }
                    @keyframes float {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-8px); }
                    }
                `}
            </style>

            <div className="w-full min-h-[calc(100vh-100px)] bg-gray-50 p-6">
                <div className="max-w-5xl mx-auto space-y-6">

                    {/* Card Assistente - Destaque Principal */}
                    <div
                        className="animate-fade-in-up rounded-2xl overflow-hidden shadow-xl border border-blue-100"
                        style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1a56db 100%)" }}
                    >
                        <div className="flex flex-col md:flex-row items-center gap-8 p-8 md:p-10">
                            {/* Logo IA */}
                            <div className="flex-shrink-0">
                                <div className="relative">
                                    <div className="absolute inset-0 bg-blue-400 rounded-full blur-2xl opacity-30" style={{ animation: "float 3s ease-in-out infinite" }}></div>
                                    <img
                                        src={logoIA}
                                        alt="Assistente IA"
                                        className="w-28 h-28 rounded-2xl relative z-10 shadow-2xl border-2 border-white/20"
                                        style={{ animation: "float 3s ease-in-out infinite" }}
                                    />
                                </div>
                            </div>

                            {/* Texto */}
                            <div className="flex-1 text-center md:text-left">
                                <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full mb-3">
                                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                                    <span className="text-white/80 text-xs font-medium">Disponível agora</span>
                                </div>
                                <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 tracking-tight">
                                    Assistente LocaSmartPro
                                </h1>
                                <p className="text-blue-100 text-base leading-relaxed mb-6 max-w-lg">
                                    Tire suas dúvidas instantaneamente. Nosso assistente conhece cada funcionalidade do sistema e está pronto para te ajudar.
                                </p>
                                <button
                                    onClick={abrirAssistente}
                                    className="group inline-flex items-center gap-2 px-7 py-3.5 bg-white text-blue-900 font-bold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 text-base"
                                >
                                    <MessageCircle size={20} />
                                    Falar com o Assistente
                                </button>
                            </div>

                            {/* Features do assistente */}
                            <div className="flex-shrink-0 hidden lg:flex flex-col gap-3">
                                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                                    <div className="bg-white/15 p-2 rounded-lg">
                                        <MessageCircle size={16} className="text-white" />
                                    </div>
                                    <span className="text-white text-sm font-medium">Respostas imediatas</span>
                                </div>
                                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                                    <div className="bg-white/15 p-2 rounded-lg">
                                        <BookOpen size={16} className="text-white" />
                                    </div>
                                    <span className="text-white text-sm font-medium">Base de conhecimento</span>
                                </div>
                                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3">
                                    <div className="bg-white/15 p-2 rounded-lg">
                                        <Headphones size={16} className="text-white" />
                                    </div>
                                    <span className="text-white text-sm font-medium">Suporte guiado</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Cards secundários */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                        {/* Card Treinamento */}
                        <a
                            href={trainingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="animate-fade-in-up ajuda-delay-1 group bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-200 no-underline"
                        >
                            <div className="bg-blue-50 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-blue-100 transition-colors">
                                <PlayCircle size={28} className="text-blue-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Central de Treinamento
                            </h2>
                            <p className="text-gray-500 text-sm leading-relaxed mb-5">
                                Acesse nossa plataforma exclusiva com todos os tutoriais em vídeo para dominar o sistema.
                            </p>
                            <span className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-sm group-hover:gap-2.5 transition-all">
                                Acessar portal
                                <ExternalLink size={14} />
                            </span>
                        </a>

                        {/* Card WhatsApp */}
                        <a
                            href="https://wa.me/5585991491356"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="animate-fade-in-up ajuda-delay-2 group bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-200 no-underline"
                        >
                            <div className="bg-green-50 w-14 h-14 rounded-xl flex items-center justify-center mb-5 group-hover:bg-green-100 transition-colors">
                                <Headphones size={28} className="text-green-600" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-2">
                                Suporte via WhatsApp
                            </h2>
                            <p className="text-gray-500 text-sm leading-relaxed mb-5">
                                Fale diretamente com nossa equipe de suporte pelo WhatsApp para resolver qualquer questão.
                            </p>
                            <span className="inline-flex items-center gap-1.5 text-green-600 font-semibold text-sm group-hover:gap-2.5 transition-all">
                                (85) 9149-1356
                                <ExternalLink size={14} />
                            </span>
                        </a>

                    </div>

                    {/* Dica do assistente */}
                    <div className="animate-fade-in-up ajuda-delay-3 bg-blue-50 border border-blue-100 rounded-xl px-6 py-4 flex items-center gap-4">
                        <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
                            <FileText size={18} className="text-blue-600" />
                        </div>
                        <p className="text-blue-800 text-sm">
                            <strong>Dica:</strong> O botão <strong>Assistente LocaSmartPro</strong> no topo da tela está sempre disponível em qualquer página do sistema. Clique nele para tirar dúvidas a qualquer momento.
                        </p>
                    </div>

                </div>
            </div>
        </LocaSmartLayout>
    );
}

export default AjudaView;